import ExcelJS from "exceljs";

const FIELD_LABELS = {
  calculationType: "Calc Type",
  calculationMethod: "Method",
  calculationBasis: "Basis",
  manualAmount: "Amount (₹/mo)",
  manualPercent: "Percent",
  manualAmountType: "Amount Type",
  percentBasis: "Percent Basis",
  selectedFormula: "Formula",
  autoAdjustCTC: "Balance CTC",
  isActive: "Active",
  statusEffectiveFrom: "Status from",
  taxTreatment: "Tax",
  pfApplicable: "PF",
  esiApplicable: "ESI",
  gratuityApplicable: "Gratuity",
  orderNo: "Order No.",
  componentNature: "Nature",
};

const CONFIG_KEYS = Object.keys(FIELD_LABELS);

function effectiveMonthOf(source) {
  const raw =
    source?.effectiveMonth ||
    source?.statusEffectiveFrom ||
    source?.snapshot?.statusEffectiveFrom ||
    "";
  const ym = String(raw).trim().slice(0, 7);
  return /^\d{4}-\d{2}$/.test(ym) ? ym : "";
}

function normalizeSnap(raw) {
  if (!raw || typeof raw !== "object") return {};
  const snap = raw.snapshot && typeof raw.snapshot === "object" ? raw.snapshot : raw;
  const out = {};
  CONFIG_KEYS.forEach((k) => {
    if (Object.prototype.hasOwnProperty.call(snap, k)) out[k] = snap[k];
  });
  return out;
}

function consolidateHistory(history) {
  const byMonth = new Map();
  (Array.isArray(history) ? history : []).forEach((raw) => {
    const snap = normalizeSnap(raw);
    const ym = effectiveMonthOf({ ...raw, ...snap });
    if (!ym) return;
    const entry = {
      id: raw.id || `h_${ym}`,
      effectiveMonth: ym,
      changedAt: raw.changedAt || "",
      note: raw.note || "",
      changedFields: Array.isArray(raw.changedFields) ? raw.changedFields : [],
      snapshot: snap,
    };
    const prev = byMonth.get(ym);
    if (!prev || String(entry.changedAt) >= String(prev.changedAt)) {
      byMonth.set(ym, entry);
    }
  });
  return [...byMonth.values()].sort((a, b) =>
    String(a.effectiveMonth).localeCompare(String(b.effectiveMonth))
  );
}

function formatValue(key, val) {
  if (val == null || val === "") return "";
  if (typeof val === "boolean") return val ? "Yes" : "No";
  if (key === "selectedFormula" && typeof val === "object") {
    return val.name || val.expression || JSON.stringify(val);
  }
  if (key === "statusEffectiveFrom") return String(val).slice(0, 7);
  return String(val);
}

function buildExportRows(structureName, components) {
  const rows = [];
  (Array.isArray(components) ? components : []).forEach((comp) => {
    const history = consolidateHistory(comp?.configHistory);
    if (!history.length) {
      const snap = normalizeSnap(comp);
      rows.push({
        structureName: structureName || "",
        componentName: comp?.name || "",
        componentCode: comp?.code || "",
        effectiveMonth: effectiveMonthOf(snap) || "",
        savedAt: "",
        changes: "",
        note: "Current (not saved to history)",
        ...snap,
      });
      return;
    }
    history.forEach((h) => {
      rows.push({
        structureName: structureName || "",
        componentName: comp?.name || "",
        componentCode: comp?.code || "",
        effectiveMonth: h.effectiveMonth,
        savedAt: h.changedAt ? new Date(h.changedAt).toLocaleString() : "",
        changes: (h.changedFields || []).join(", "),
        note: h.note || "",
        ...h.snapshot,
      });
    });
  });
  rows.sort((a, b) => {
    const n = String(a.componentName).localeCompare(String(b.componentName));
    if (n !== 0) return n;
    return String(b.effectiveMonth).localeCompare(String(a.effectiveMonth));
  });
  return rows;
}

function safeFilename(name) {
  return String(name || "salary-structure")
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .slice(0, 60) || "salary-structure";
}

/**
 * Export all component configuration history rows (effective month × full settings).
 */
export async function exportStructureComponentConfigHistory({
  structureName = "",
  components = [],
}) {
  const rows = buildExportRows(structureName, components);
  const wb = new ExcelJS.Workbook();
  wb.creator = "PaceHRM";
  const ws = wb.addWorksheet("Component Config History");

  const metaCols = [
    { header: "Structure", key: "structureName", width: 22 },
    { header: "Component", key: "componentName", width: 22 },
    { header: "Code", key: "componentCode", width: 14 },
    { header: "Effective Month", key: "effectiveMonth", width: 14 },
    { header: "Saved At", key: "savedAt", width: 20 },
    { header: "Changes", key: "changes", width: 28 },
    { header: "Remarks", key: "note", width: 32 },
  ];
  const configCols = CONFIG_KEYS.map((k) => ({
    header: FIELD_LABELS[k],
    key: k,
    width: k === "selectedFormula" ? 24 : 14,
  }));

  ws.columns = [...metaCols, ...configCols];

  ws.getRow(1).font = { bold: true };
  ws.getRow(1).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFE8F4F8" },
  };

  rows.forEach((row) => {
    const line = {
      structureName: row.structureName,
      componentName: row.componentName,
      componentCode: row.componentCode,
      effectiveMonth: row.effectiveMonth,
      savedAt: row.savedAt,
      changes: row.changes,
      note: row.note,
    };
    CONFIG_KEYS.forEach((k) => {
      line[k] = formatValue(k, row[k]);
    });
    ws.addRow(line);
  });

  ws.views = [{ state: "frozen", ySplit: 1 }];

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const filename = `${safeFilename(structureName)}-component-config-history.xlsx`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
  return filename;
}

export { FIELD_LABELS, CONFIG_KEYS, consolidateHistory as consolidateComponentHistory };
